import type { DeliveryEstimate } from './DeliveryCell';
import type { RouteFigure } from './routeFigure';
import axiosNest from '../../api/axiosNest';
import type { OrderPointGeo, PositionPlace } from './geoPoint';
import type { PriceComparison } from './priceStatistics';
import type { TruckBanCheck } from '../truckBans/truckBansApi';

/**
 * Podpowiedzi pojazdów pod zlecenie — odczyt.
 *
 * Odpowiada `GET /user-offers/:id/suggestions`. Osobno od `orderActionsApi.ts`,
 * bo tam są wyłącznie czynności zmieniające rekord — podpowiedź niczego nie
 * zmienia i obsadzenie zostaje osobną, jawną decyzją.
 */

export interface DeadheadEstimate {
  straightKm: number;
  roadKm: number;
  multiplier: number;
}

/**
 * „Dostępny od" kierowcy — oszacowanie z limitów czasu jazdy i nieobecności
 * (backend: `user-offers/dostepnosc-kierowcy.ts`). `null` = brak danych.
 */
export interface DriverAvailability {
  sources: string[];
  measuredAt: string | null;
  availableFrom: string;
  availableNow: boolean;
  constraint: string | null;
  description: string | null;
  remaining: {
    todayH: number | null;
    weekH: number | null;
    twoWeeksH: number | null;
    untilBreakH: number | null;
  };
  nextAbsence: { od: string; do: string; type: string | null } | null;
  zalozenia: string[];
  /**
   * Fakty z osi czasu tachografu (06.10.2026); brak = starsze API albo
   * liczone z samych liczników.
   */
  timelineUntil?: string | null;
  shift?: DriverShift | null;
  weeklyRestDueBy?: string | null;
  reliefs?: { extendedDaysLeft: number | null; reducedRestsLeft: number | null } | null;
}

/** Bieżąca zmiana kierowcy z osi czasu (backend: `DriverShift`). */
export interface DriverShift {
  startedAt: string;
  dutyH: number;
  dutyLeftH: number;
  drivenH: number | null;
  workedH: number | null;
  workedSinceBreakH: number | null;
  workBreaksH: number | null;
  extendedToday: boolean;
  splitBreakStarted: boolean | null;
}

/**
 * Koszt dolotu w PLN wg stawek spółki pojazdu (`user-offers/access-cost.ts`).
 * `totalPln === null` = brakuje parametru od Marathonu — koszt nieznany,
 * nie zerowy. Składnik obok dolotu; kolejność kandydatów go nie uwzględnia.
 */
export interface AccessCost {
  totalPln: number | null;
  components: {
    fuelPln: number | null;
    driverPln: number | null;
    tractorPln: number | null;
  };
  missing: string[];
  /**
   * Rachunek składników, z kursem NBP przy stawkach w EUR (30.09.2026).
   * Brak = starsze API.
   */
  breakdown?: string[];
  assumptions: string[];
}

/**
 * Pasmo alertu marży (`user-offers/margin.ts`): cztery z progów albo pięć pasm
 * od kosztu (paczka p. Mariusza 21.10, przełącznik `marginFiveBands`).
 */
export type MarginBand =
  | 'high'
  | 'medium'
  | 'breakEven'
  | 'clearlyNegative'
  | 'criticalLoss'
  | 'loss'
  | 'minimum'
  | 'normal'
  | 'verify';

/**
 * Marża kandydata: wartość zlecenia minus koszt dolotu i trasy wg stawek
 * spółki pojazdu. `pln === null` = brak składnika (w `missing`), nie zero.
 * `band` — alert wg progów z parametrów; `null` = progi puste (alert
 * wyłączony) albo marża między pasmami. Alert, nie ranking.
 */
export interface CandidateMargin {
  totalCostPln: number | null;
  pln: number | null;
  pct: number | null;
  missing: string[];
  breakdown: string[];
  band: MarginBand | null;
  bandRule: string | null;
  /** Pięć pasm: marża w % kosztu własnego, z którego wyszło pasmo; brak przy progach. */
  bandPct?: number | null;
  /**
   * Koszt dolotu i trasy po składowych (15.10.2026, makieta FTL Etap 1;
   * backend: `cost-split.ts`). Brak = przełącznik `recommendationDetails`
   * wyłączony albo starsze API.
   */
  costSplit?: CostSplit;
}

/** Składowe kosztu w PLN; `null` = nieznane (nigdy zero). */
export interface CostLine {
  fuelPln: number | null;
  driverPln: number | null;
  tractorPln: number | null;
  /** Brak pola = opłaty nie wchodzą do rachunku (bez tras HERE). */
  tollsPln?: number | null;
  totalPln: number | null;
}

/** Trasa zlecenia = koszt całkowity − dolot; `null`, gdy trasy nie znamy. */
export interface CostSplit {
  access: CostLine;
  route: CostLine | null;
  total: CostLine | null;
}

/**
 * Wykonalność pary (15.10.2026, makieta FTL Etap 1; backend:
 * `user-offers/feasibility.ts`, przełącznik `recommendationFeasibility`).
 * Kategoria przepisana z werdyktów „zdąży” i „dowiezie” plus zapas do okna —
 * nie ocena (to `PairScore`) ani ranking.
 */
export type FeasibilityGrade = 'fits' | 'tight' | 'unknown' | 'late';

export interface Feasibility {
  grade: FeasibilityGrade;
  /** „mieści się”, „ciasny dolot”, „tylko z ulgami”, „brak danych”, „po oknie”. */
  label: string;
  reasons: string[];
  tightThresholdH: number;
}

/** Zapas do początku okna załadunku; `hours === null` z powodem. */
export interface WindowReserve {
  hours: number | null;
  arrivalAt: string | null;
  reason: string | null;
}

/** Gdzie i kiedy auto wolne po zleceniu — szacunek; `freeAt === null` z powodem. */
export interface AfterOrder {
  freeAt: string | null;
  place: string | null;
  reason: string | null;
}

/**
 * Ocena pary zlecenie–pojazd 0–10 (13.10.2026, backend: `user-offers/pair-score.ts`,
 * przełącznik `pairScoring`). Liczba z jednym miejscem po przecinku ze
 * składowymi (marża, wykonalność czasowa, dolot, priorytet klienta; zadania
 * strategiczne później). `score === null` = nie da się ocenić (powód
 * w `reason`). Ocena nie zmienia kolejności z API (po dolocie) i nikogo nie
 * wyklucza — to co innego niż trzystopniowa „wykonalność” na karcie pojazdu.
 */
export interface PairScore {
  score: number | null;
  reason: string | null;
  used: number;
  of: number;
  /** „ocena z 3 z 4 składowych”. */
  coverage: string;
  components: Array<{
    key: string;
    label: string;
    value: number | null;
    weight: number;
    share: number | null;
    text: string;
  }>;
  skipped: Array<{ key: string; label: string; reason: string }>;
}

export interface VehicleCandidate {
  carId: number;
  registrationNumber: string | null;
  makeAndModel: string | null;
  carType: string | null;
  companyId: number | null;
  /** Opiekun FTL auta (`Car.ftlCaretaker`). Brak = starsze API. */
  ftlCaretaker?: string | null;
  driverName: string | null;
  /** Brak = starsze API; `null` = brak danych o czasie pracy. */
  driverAvailability?: DriverAvailability | null;
  accessDistance: DeadheadEstimate;
  /**
   * Dolot z podpisanym źródłem — trasa z map HERE albo szacunek, z rachunkiem
   * poglądowym (06.10.2026). Brak = starsze API, zostaje `accessDistance`.
   */
  accessRoute?: RouteFigure;
  /** Brak = starsze API. */
  accessCost?: AccessCost;
  /** Marża z pasmem alertu (01.10.2026). Brak = starsze API. */
  margin?: CandidateMargin;
  /** Ocena pary 0–10 (13.10.2026). Brak = przełącznik `pairScoring` wyłączony albo starsze API. */
  pairScore?: PairScore;
  /** Odrzucenie planisty z powodem — oznaczenie, nie wykluczenie. Brak = starsze API. */
  rejected?: { reason: string | null; at: string; by: string | null } | null;
  position: {
    lat: number;
    lng: number;
    freeFrom: string;
    /** `TELEMATYKA` (GPS z eco-trucka) albo `HISTORIA_ZLECEN`. */
    zrodlo: string;
    fromOrder: string | null;
    /** Id zlecenia `fromOrder` — link „Wolny od” do jego karty (01.10.2026). Brak = starsze API. */
    fromOrderId?: number | null;
    /** Czas odczytu GPS (ISO); `null` dla pozycji z historii. */
    measuredAt?: string | null;
    /** Miejscowość przy GPS (06.10.2026). Brak = starsze API albo wyłączone geokodowanie. */
    place?: PositionPlace | null;
    /** Adres pozycji — przy historii rozładunek poprzedniego zlecenia. */
    adres?: PositionAddress | null;
  };
  spareHours: number;
  /**
   * Czy pojazd zdąży na załadunek (`user-offers/on-time-verdict.ts`).
   * `zdazy === null` znaczy „nie wiemy”, nie „nie zdąży”, a `pewne`
   * mówi, czy werdykt opiera się na danych klienta, czy na naszym
   * wyliczeniu. Starsza odpowiedź API może go nie mieć.
   */
  onTime?: {
    willMakeIt: boolean | null;
    reason: string | null;
    certain: boolean;
  };
  /**
   * Czy dowiezie przed końcem okna rozładunku (`user-offers/delivery-verdict.ts`,
   * „pełne tacho”). Zawsze oszacowanie; `willMakeIt === null` = nie wiemy.
   * Starsza odpowiedź API może go nie mieć.
   */
  delivery?: DeliveryEstimate;
  ostrzezenia: string[];
  /** Inne zlecenia, do których auto już kandyduje — konflikt do pokazania, nie blokada. */
  candidateFor: Array<{ userOfferId: number; externalId: string | null }>;
  /** Na jakiej podstawie — uzasadnienie punkt po punkcie. Brak = starsze API. */
  basis?: Array<{ label: string; text: string }>;
  /** Zapas czasu pracy kierowcy po wykonaniu zlecenia (06.10.2026). Brak = starsze API. */
  workReserve?: WorkReserve;
  /** Wykonalność (15.10.2026). Brak = przełącznik wyłączony albo starsze API. */
  feasibility?: Feasibility;
  /** Zapas do okna załadunku (15.10.2026). Brak = przełącznik wyłączony. */
  windowReserve?: WindowReserve;
  /** Po zleceniu: gdzie i kiedy wolny (15.10.2026). Brak = przełącznik wyłączony. */
  afterOrder?: AfterOrder;
  /**
   * Realizacja pewnie poza oknami zlecenia (21.10.2026, `variantWithinWindows`)
   * — „Wariant” i „Obsadź” (gdy zakłada wariant) wyszarzone z tym powodem.
   * Brak = wolno.
   */
  variantRefusal?: string;
  /**
   * Obie liczby spóźnienia przy odmowie (22.10.2026, decyzja użytkownika
   * pkt 4) — kształt `LatenessFigures` (`variantWindow.ts`); czytamy przez
   * `latenessOf`. Brak = bez odmowy albo starsze API.
   */
  variantLateness?: unknown;
  /**
   * Pojazd zajęty innym obsadzeniem w tym terminie — na końcu listy,
   * z oznaczeniem (06.10.2026). Brak = wolny (albo starsze API).
   * `untilSource: 'tmsPlan'` — „do …” z planu TMS przy punktach (12.10.2026,
   * tylko tekst); brak pola — szacunek.
   */
  busyWith?: BusyWith;
}

/** Czym zajęty pojazd (backend: `SerializedBusy`). */
export interface BusyWith {
  userOfferId: number | null;
  externalId: string | null;
  until: string | null;
  untilSource?: 'tmsPlan';
}

/**
 * Zapas czasu pracy kierowcy po zleceniu (backend: `WorkReserve`
 * w `user-offers/delivery-verdict.ts`). `hours === null` = nie liczymy,
 * powód w `reason` — nigdy zero.
 */
export interface WorkReserve {
  hours: number | null;
  limitedBy: 'driving' | 'duty' | 'week' | null;
  drivingLeftH: number | null;
  dutyLeftH: number | null;
  weekLeftH: number | null;
  finishedAt: string | null;
  restOnTheWay: boolean;
  reason: string | null;
  assumptions: string[];
}

/** Adres pozycji z API (`PositionAddress`): pełny przy rozładunku, przy GPS sam kraj. */
export interface PositionAddress {
  country: string | null;
  postalCode: string | null;
  city: string | null;
}

export interface SuggestionsResult {
  candidates: VehicleCandidate[];
  skipped: Array<{
    carId: number;
    registrationNumber: string | null;
    /** Klucz powodu (dane) — do grupowania, nie do pokazania. */
    reason: string;
    /** Powód po polsku z API (07.10.2026); brak = starsze API, wtedy klucz. */
    reasonText?: string;
  }>;
  skippedSummary: Record<string, number>;
  /** Podpisy grup z `skippedSummary` po polsku, z API (07.10.2026). */
  skippedLabels?: Record<string, string>;
  order: {
    id: number;
    externalId: string | null;
    fromCity: string | null;
    fromDate: string;
    toDate: string;
    /** Status w TMS (W/P/C/Z); przy `P` zlecenie ma już wykonawcę. Brak = starsze API. */
    tmsOrderStatus?: string | null;
    /** Trasa zlecenia z podpisanym źródłem (06.10.2026); `null` = nie liczymy. Brak = starsze API. */
    route?: RouteFigure | null;
    /**
     * Punkt załadunku / rozładunku z geokodowania (06.10.2026) — dolot
     * do środka obszaru, nie do adresu. `null` = punkt z TMS. Brak = starsze API.
     */
    pickupGeo?: OrderPointGeo | null;
    deliveryGeo?: OrderPointGeo | null;
    /**
     * Zakazy ruchu ciężarówek w kraju załadunku i rozładunku w oknach
     * zlecenia (19.10.2026, przełącznik `truckBanCalendar`) — ostrzeżenie
     * nad listą. Brak = przełącznik wyłączony albo starsze API.
     */
    truckBans?: TruckBanCheck | null;
  };
  positionWindowDays: number;
  /** Wiek odczytu GPS, który jeszcze jest pozycją (parametr, h). Brak = starsze API. */
  maxPositionAgeHours?: number;
  /** Czas pracy kierowców wyłączony flagą — „zdąży” dla wypoczętego kierowcy. Brak = starsze API. */
  driverHoursEnabled?: boolean;
  /** Załadunek za horyzontem — pozycje „na dziś” niemiarodajne. Brak = starsze API. */
  horizon?: { beyond: boolean; daysAhead: number; horizonDays: number };
  /**
   * „Tylko moje pojazdy” (`mine=1`, 02.10.2026): API zawęża listę do aut
   * opiekuna z konta pytającego, zanim obetnie ją do pierwszej dziesiątki.
   * `requested && !applied` = na koncie nie wskazano opiekuna, lista jest
   * całej floty. `caretakerSet: null` — bez `mine=1` konta nie sprawdzano.
   * Brak = starsze API.
   */
  mine?: { requested: boolean; applied: boolean; caretakerSet: boolean | null };
  /**
   * Ilu pojazdów innych opiekunów jest w pełnej liście rekomendacji, gdy
   * API zawęziło ją do aut planisty; `null` bez zawężenia. Brak = starsze API.
   */
  otherCaretakersCount?: number | null;
  /**
   * Statystyka cen z VIA z kosztem własnym pierwszego auta z listy
   * (13.10.2026) — tylko z przełącznikiem `priceStatistics`; brak = wyłączony.
   */
  priceStatistics?: PriceComparison | null;
}

export interface FetchSuggestionsOptions {
  /** Długość listy; bez — domyślna z API (10). */
  limit?: number;
  /** „Tylko moje pojazdy” — opiekuna API czyta z konta, nie z zapytania. */
  mine?: boolean;
}

export async function fetchSuggestions(
  id: number | string,
  options: FetchSuggestionsOptions = {},
): Promise<SuggestionsResult> {
  const params: Record<string, number> = {};
  if (options.limit) params.limit = options.limit;
  if (options.mine) params.mine = 1;
  const { data } = await axiosNest.get<SuggestionsResult>(
    `/user-offers/${id}/suggestions`,
    { params: Object.keys(params).length > 0 ? params : undefined },
  );
  return data;
}
